import React, { useState } from 'react';
import { Card, Button, Typography, Grid } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface BlocksSelectionCardProps {
  onNext: (selectedOption: string) => void;
}

export const BlocksSelectionCard: React.FC<BlocksSelectionCardProps> = ({
  onNext,
}) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const screens = Grid.useBreakpoint();

  const blockOptions = [
    { value: '1', label: '1', subtitle: t('onboarding:cards.blocksSelection.options.1') },
    { value: '2', label: '2', subtitle: t('onboarding:cards.blocksSelection.options.2') },
    { value: '3', label: '3', subtitle: t('onboarding:cards.blocksSelection.options.3') },
    { value: '5+', label: '5+', subtitle: t('onboarding:cards.blocksSelection.options.5+') },
    { value: '10+', label: '10+', subtitle: t('onboarding:cards.blocksSelection.options.10+') },
  ];

  const handleOptionSelect = (option: string) => {
    setSelectedOption(option);
  };

  const handleNext = () => {
    if (selectedOption) {
      onNext(selectedOption);
    }
  };

  const isCompleted = selectedOption !== '';

  return (
    <Card
      style={{
        width: '100%',
        maxWidth: 600,
        margin: '0 auto',
        border: isCompleted ? '1px solid #52c41a' : '1px solid #1890ff',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      }}
      styles={{ body: { padding: '32px' } }}
    >
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <div style={{ marginBottom: '16px' }}>
          <HomeOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
        </div>
        <Text style={{ fontSize: '18px', fontWeight: 600, color: '#1890ff' }}>
          {t('onboarding:cards.blocksSelection.title')}
        </Text>
      </div>

      {/* Block Options */}
      <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'center', padding: '0 16px' }}>
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: screens.xs ? '12px' : '16px',
          maxWidth: screens.xs ? '100%' : '400px',
          width: '100%'
        }}>
          {/* First row: 1, 2, 3 */}
          <div style={{ 
            display: 'flex', 
            gap: screens.xs ? '8px' : '16px',
            justifyContent: 'center'
          }}>
            {blockOptions.slice(0, 3).map((option) => (
              <Button
                key={option.value}
                type={selectedOption === option.value ? 'primary' : 'default'}
                size="large"
                onClick={() => handleOptionSelect(option.value)}
                style={{
                  flex: 1,
                  height: screens.xs ? '70px' : '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  border: selectedOption === option.value 
                    ? '2px solid #1890ff' 
                    : '2px solid #d9d9d9',
                  backgroundColor: selectedOption === option.value 
                    ? '#1890ff' 
                    : '#fff',
                  color: selectedOption === option.value 
                    ? '#fff' 
                    : '#595959',
                  transition: 'all 0.3s ease',
                  boxShadow: selectedOption === option.value 
                    ? '0 4px 12px rgba(24, 144, 255, 0.3)' 
                    : '0 2px 4px rgba(0, 0, 0, 0.1)',
                  fontSize: screens.xs ? '16px' : '18px',
                  fontWeight: 'bold'
                }}
                onMouseEnter={(e) => {
                  if (selectedOption !== option.value) {
                    e.currentTarget.style.borderColor = '#1890ff';
                    e.currentTarget.style.color = '#1890ff';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedOption !== option.value) {
                    e.currentTarget.style.borderColor = '#d9d9d9';
                    e.currentTarget.style.color = '#595959';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                  }
                }}
              >
                <div style={{ fontSize: screens.xs ? '16px' : '18px', fontWeight: 'bold', marginBottom: screens.xs ? '2px' : '4px' }}>
                  {option.label}
                </div>
                <div style={{ fontSize: screens.xs ? '10px' : '12px', opacity: 0.8 }}>
                  {option.subtitle}
                </div>
              </Button>
            ))}
          </div>
          
          {/* Second row: 5+, 10+ (centered) */}
          <div style={{ 
            display: 'flex', 
            gap: screens.xs ? '8px' : '16px',
            justifyContent: 'center'
          }}>
            {blockOptions.slice(3).map((option) => (
              <Button
                key={option.value}
                type={selectedOption === option.value ? 'primary' : 'default'}
                size="large"
                onClick={() => handleOptionSelect(option.value)}
                style={{
                  flex: 1,
                  maxWidth: screens.xs ? '120px' : '150px',
                  height: screens.xs ? '70px' : '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  border: selectedOption === option.value 
                    ? '2px solid #1890ff' 
                    : '2px solid #d9d9d9',
                  backgroundColor: selectedOption === option.value 
                    ? '#1890ff' 
                    : '#fff',
                  color: selectedOption === option.value 
                    ? '#fff' 
                    : '#595959',
                  transition: 'all 0.3s ease',
                  boxShadow: selectedOption === option.value 
                    ? '0 4px 12px rgba(24, 144, 255, 0.3)' 
                    : '0 2px 4px rgba(0, 0, 0, 0.1)',
                  fontSize: screens.xs ? '16px' : '18px',
                  fontWeight: 'bold'
                }}
                onMouseEnter={(e) => {
                  if (selectedOption !== option.value) {
                    e.currentTarget.style.borderColor = '#1890ff';
                    e.currentTarget.style.color = '#1890ff';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedOption !== option.value) {
                    e.currentTarget.style.borderColor = '#d9d9d9';
                    e.currentTarget.style.color = '#595959';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                  }
                }}
              >
                <div style={{ fontSize: screens.xs ? '16px' : '18px', fontWeight: 'bold', marginBottom: screens.xs ? '2px' : '4px' }}>
                  {option.label}
                </div>
                <div style={{ fontSize: screens.xs ? '10px' : '12px', opacity: 0.8 }}>
                  {option.subtitle}
                </div>
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Next Button */}
      <div style={{ textAlign: 'center' }}>
        <Button 
          type="primary" 
          size="large"
          onClick={handleNext}
          disabled={!isCompleted}
          style={{ 
            minWidth: '120px',
            backgroundColor: isCompleted ? '#52c41a' : '#1890ff',
            borderColor: isCompleted ? '#52c41a' : '#1890ff',
            color: '#fff',
            fontWeight: 'bold'
          }}
        >
{t('onboarding:cards.blocksSelection.next')}
        </Button>
      </div>

      {/* Completion Status */}
      {isCompleted && (
        <div style={{ 
          marginTop: '16px', 
          textAlign: 'center',
          padding: '12px',
          backgroundColor: '#f6ffed',
          borderRadius: '6px',
          border: '1px solid #b7eb8f'
        }}>
          <Text style={{ color: '#52c41a', fontSize: '14px' }}>
            {t(`onboarding:cards.blocksSelection.selected.${selectedOption}`)}
          </Text>
        </div>
      )}
    </Card>
  );
};
